import React from 'react';

const horizonLabel = (offset) => {
  if (offset == null) return 'horizon';
  if (offset === 0) return 'Intraday';
  if (offset === 1) return 'Day-Ahead (D+1)';
  return `D+${offset}`;
};

// Forecaster-facing docs (mkdocs, served at /docs/). The penalty/qualification
// mechanics are explained there — link straight to the relevant section
// instead of exposing the engine's internal "Rule N" numbering.
const DOCS = {
  intradayQualification: '/docs/evaluation_intraday/#daily-score-and-qualification',
  dnQualification: '/docs/evaluation_dn/#qualification',
  observationGaps: '/docs/evaluation/',
};

const DocsLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="underline font-medium hover:no-underline"
  >
    {children}
  </a>
);

/**
 * Three states:
 *   - has score row, value != null, !is_penalty            → green "qualified"
 *   - has score row, is_penalty=true (value usually null)  → red Q75 banner
 *   - no score row at all (incomplete observations)        → grey "skipped"
 */
export default function DayPenaltyBanner({ scoreRow, horizonOffset }) {
  if (!scoreRow) {
    return (
      <div className="rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870/40 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
        <div className="font-semibold">Day skipped</div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          No score was emitted — the observation series for this day was incomplete, so the day is
          skipped for everyone. No penalty is applied; the day simply does not count.{' '}
          <DocsLink href={DOCS.observationGaps}>How scoring handles observation gaps →</DocsLink>
        </p>
      </div>
    );
  }

  if (scoreRow.is_penalty) {
    const isIntraday = horizonOffset === 0;
    const ruleHeadline = isIntraday
      ? 'Day failed slot-coverage qualification'
      : 'Day failed session-level compliance';
    const ruleBody = isIntraday
      ? 'At least one slot for some target timestamp could not be filled by a live submission, intraday forward-fill, or cross-horizon substitute. The peer 75th-percentile day score is applied as a penalty.'
      : 'The forecaster did not submit a full (q10, q50, q90) to every challenge opened for this target day and horizon. The peer 75th-percentile day score is applied as a penalty.';
    const docsHref = isIntraday ? DOCS.intradayQualification : DOCS.dnQualification;

    return (
      <div className="rounded-md border border-red-300 dark:border-red-700/60 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-900 dark:text-red-200">
        <div className="font-semibold">Peer penalty applied · {horizonLabel(horizonOffset)}</div>
        <p className="mt-1 text-xs text-red-800/90 dark:text-red-300/90">
          {ruleHeadline}. {ruleBody}{' '}
          <DocsLink href={docsHref}>How qualification works →</DocsLink>
        </p>
      </div>
    );
  }

  // Qualified state is conveyed by a compact chip in the drawer header — no
  // banner needed for the happy path. Save the big banner for problems.
  return null;
}
